import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Download, Loader2, Search, Globe, Filter, 
  ChevronLeft, ChevronRight, RefreshCw, FileSpreadsheet,
  MapPin, Tag, DollarSign, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicCatalog = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [catalogInfo, setCatalogInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [countries, setCountries] = useState([]);
  const [downloading, setDownloading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    country: searchParams.get("country") || "",
    category: searchParams.get("category") || "",
    minPrice: searchParams.get("min_price") || "",
    maxPrice: searchParams.get("max_price") || "",
    search: searchParams.get("search") || ""
  });
  
  const [page, setPage] = useState(parseInt(searchParams.get("page")) || 1);
  const limit = 50;

  useEffect(() => {
    if (token) {
      loadCatalogInfo();
      loadCountries();
    }
  }, [token]);

  useEffect(() => {
    if (token && catalogInfo) {
      loadProducts();
    }
  }, [token, catalogInfo, page, filters]);

  const loadCatalogInfo = async () => {
    try {
      const response = await fetch(`${API}/catalog-api/v1/catalog/stats?api_key=${token}`);
      const data = await response.json();
      
      if (response.ok) {
        setCatalogInfo(data);
        setError(null);
      } else {
        setError(data.detail || "Token inválido o expirado");
      }
    } catch (err) {
      setError("Error de conexión");
    }
  };

  const loadCountries = async () => {
    try {
      const response = await fetch(`${API}/catalog-api/v1/catalog/countries?api_key=${token}`);
      const data = await response.json();
      if (response.ok) {
        setCountries(data.countries || []);
      }
    } catch (err) {
      console.error("Error loading countries:", err);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        api_key: token,
        limit: limit.toString(),
        offset: ((page - 1) * limit).toString()
      });

      if (filters.country) params.append("country", filters.country);
      if (filters.category) params.append("category", filters.category);
      if (filters.minPrice) params.append("min_price", filters.minPrice);
      if (filters.maxPrice) params.append("max_price", filters.maxPrice);

      const response = await fetch(`${API}/catalog-api/v1/catalog?${params}`);
      const data = await response.json();

      if (response.ok) {
        setProducts(data.products || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error("Error loading products:", err);
    }
    setLoading(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ api_key: token });
      if (filters.country) params.append("country", filters.country);
      if (filters.category) params.append("category", filters.category);
      if (filters.minPrice) params.append("min_price", filters.minPrice);
      if (filters.maxPrice) params.append("max_price", filters.maxPrice);

      const response = await fetch(`${API}/catalog-api/v1/catalog/download?${params}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `catalogo_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Catálogo descargado");
      } else {
        toast.error("Error al descargar");
      }
    } catch (err) {
      toast.error("Error de conexión");
    }
    setDownloading(false);
  };

  const applyFilters = () => {
    setPage(1);
    const params = new URLSearchParams();
    if (filters.country) params.set("country", filters.country);
    if (filters.category) params.set("category", filters.category);
    if (filters.minPrice) params.set("min_price", filters.minPrice);
    if (filters.maxPrice) params.set("max_price", filters.maxPrice);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      country: "",
      category: "",
      minPrice: "",
      maxPrice: "",
      search: ""
    });
    setPage(1);
    setSearchParams({});
  };

  const totalPages = Math.ceil(total / limit);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold mb-2">Acceso Denegado</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Catálogo de Productos</h1>
                {catalogInfo && (
                  <p className="text-sm text-muted-foreground">
                    {catalogInfo.total_products?.toLocaleString()} productos disponibles
                  </p>
                )}
              </div>
            </div>
            <Button 
              onClick={handleDownload} 
              disabled={downloading}
              className="bg-green-600 hover:bg-green-700"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Descargar Excel
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">País</label>
                <Select value={filters.country || "all"} onValueChange={(v) => setFilters({...filters, country: v === "all" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los países" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los países</SelectItem>
                    {countries.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Categoría</label>
                <Input
                  placeholder="Buscar categoría..."
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Precio mín.</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.minPrice}
                  onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Precio máx.</label>
                <Input
                  type="number"
                  placeholder="Sin límite"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
                />
              </div>
              
              <div className="flex items-end gap-2">
                <Button onClick={applyFilters} className="flex-1">
                  <Search className="w-4 h-4 mr-2" />
                  Filtrar
                </Button>
                <Button variant="outline" onClick={clearFilters}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        {catalogInfo && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-2xl font-bold text-blue-600">{total.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Productos encontrados</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-2xl font-bold text-green-600">{countries.length}</p>
                <p className="text-xs text-muted-foreground">Países</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-2xl font-bold text-purple-600">
                  {catalogInfo.price_stats?.min_price?.toFixed(0) || 0}€
                </p>
                <p className="text-xs text-muted-foreground">Precio mínimo</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-2xl font-bold text-orange-600">
                  {catalogInfo.price_stats?.avg_price?.toFixed(0) || 0}€
                </p>
                <p className="text-xs text-muted-foreground">Precio medio</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Products Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Website</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">País</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Categoría</th>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground">Precio</th>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground">DR</th>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground">Traffic</th>
                        <th className="text-center p-3 text-xs font-medium text-muted-foreground">Idioma</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {products.map((product, idx) => (
                        <tr key={product.id || idx} className="hover:bg-muted/30 transition-colors">
                          <td className="p-3">
                            <a 
                              href={`https://${product.website}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline font-medium"
                            >
                              {product.website}
                            </a>
                          </td>
                          <td className="p-3 text-sm">{product.country}</td>
                          <td className="p-3 text-sm text-muted-foreground max-w-xs truncate">
                            {product.category}
                          </td>
                          <td className="p-3 text-right font-medium">{product.pvp}€</td>
                          <td className="p-3 text-right">{product.dr || '-'}</td>
                          <td className="p-3 text-right">{product.traffic?.toLocaleString() || '-'}</td>
                          <td className="p-3 text-center text-sm">{product.language}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Mostrando {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} de {total.toLocaleString()}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="flex items-center px-3 text-sm">
                      {page} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Download CTA */}
        <Card className="mt-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <FileSpreadsheet className="w-10 h-10 opacity-80" />
                <div>
                  <h3 className="font-bold text-lg">¿Quieres todos los datos?</h3>
                  <p className="text-white/80 text-sm">
                    Descarga el catálogo completo en Excel con {total.toLocaleString()} productos
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleDownload} 
                disabled={downloading}
                variant="secondary"
                size="lg"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Descargar Excel
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PublicCatalog;
